import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import apiClient from "../../../services/api";
import { REGION_OPTIONS } from "../../../constants/grants.constants";
import { toVoivodeshipSlug } from "../../../utils/text.utils";
import { CompetitionAutocomplete, CompetitionOption } from "./CompetitionAutocomplete";
import { OperatorAutocomplete, OperatorOption } from "./OperatorAutocomplete";
import { RegionsMultiSelect } from "./RegionsMultiSelect";
import { SubregionsMultiSelect, District } from "./SubregionsMultiSelect";

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string | null;
}

interface ProjectFormData {
  name: string;
  competitionId: string;
  operatorId: string;
  projectUrl: string;
  regions: string[];
  subregions: string[];
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<ProjectFormData>({
    name: "",
    competitionId: "",
    operatorId: "",
    projectUrl: "",
    regions: [],
    subregions: [],
    startDate: "",
    endDate: "",
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const { data: competitionsData, isLoading: isLoadingCompetitions } = useQuery({
    queryKey: ["grants-competitions"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/competitions");
      return data;
    },
    enabled: isOpen,
  });

  const { data: operatorsData, isLoading: isLoadingOperators } = useQuery({
    queryKey: ["grants-operators"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/operators");
      return data;
    },
    enabled: isOpen,
  });

  const { data: projectData, isLoading: isLoadingProject } = useQuery({
    queryKey: ["grants-project", projectId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/grants/projects/${projectId}`);
      return data;
    },
    enabled: !!projectId && isOpen,
  });

  // Fetch districts for selected regions
  const {
    data: districtsData,
    isLoading: isLoadingDistricts,
    isError: isDistrictsError,
    refetch: refetchDistricts,
  } = useQuery({
    queryKey: ["grants-districts", formData.regions],
    queryFn: async () => {
      if (formData.regions.length === 0) return { districts: [] as District[] };

      // Fetch districts for each selected region in parallel
      const promises = formData.regions.map(async (region) => {
        const slug = toVoivodeshipSlug(region);
        const { data } = await apiClient.get(`/grants/districts?voivodeship=${slug}`);
        return data.districts as District[];
      });

      const results = await Promise.all(promises);
      const allDistricts = results.flat();

      // Sort by name
      allDistricts.sort((a, b) => a.name.localeCompare(b.name, "pl"));

      return { districts: allDistricts };
    },
    enabled: isOpen && formData.regions.length > 0,
  });

  useEffect(() => {
    if (projectData) {
      setFormData({
        name: projectData.name || "",
        competitionId: projectData.competition?.id || "",
        operatorId: projectData.operator?.id || "",
        projectUrl: projectData.projectUrl || "",
        regions: projectData.regions || [],
        subregions: projectData.subregions || [],
        startDate: projectData.startDate || "",
        endDate: projectData.endDate || "",
        isActive: projectData.isActive ?? true,
      });
    }
  }, [projectData]);

  // Clean up subregions when regions change (keep only valid ones)
  const prevRegionsRef = useRef<string[]>([]);
  useEffect(() => {
    // Skip on initial load or when loading project data
    if (prevRegionsRef.current.length === 0 && formData.regions.length > 0) {
      prevRegionsRef.current = formData.regions;
      return;
    }

    // Check if regions actually changed
    const regionsChanged =
      prevRegionsRef.current.length !== formData.regions.length ||
      !prevRegionsRef.current.every((r) => formData.regions.includes(r));

    if (regionsChanged && districtsData?.districts) {
      const validCodes = new Set(districtsData.districts.map((d) => d.code));
      setFormData((prev) => {
        const filteredSubregions = prev.subregions.filter((code) =>
          validCodes.has(code)
        );
        if (filteredSubregions.length !== prev.subregions.length) {
          return { ...prev, subregions: filteredSubregions };
        }
        return prev;
      });
    }

    prevRegionsRef.current = formData.regions;
  }, [formData.regions, districtsData]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.post("/grants/projects", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-projects"] });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.put(`/grants/projects/${projectId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-projects"] });
      queryClient.invalidateQueries({
        queryKey: ["grants-project", projectId],
      });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      competitionId: "",
      operatorId: "",
      projectUrl: "",
      regions: [],
      subregions: [],
      startDate: "",
      endDate: "",
      isActive: true,
    });
    prevRegionsRef.current = [];
    setErrors({});
    setGlobalError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setGlobalError(null);

    const payload: Record<string, unknown> = {
      name: formData.name,
      competitionId: formData.competitionId,
      operatorId: formData.operatorId,
      projectUrl: formData.projectUrl || null,
      regions: formData.regions,
      subregions: formData.subregions,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
    };

    if (projectId) {
      payload.isActive = formData.isActive;
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createMutation.isPending && !updateMutation.isPending) {
        resetForm();
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, createMutation.isPending, updateMutation.isPending]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen && !isLoadingProject) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isLoadingProject]);

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-medium text-neutral-900">
            {projectId ? "Edytuj projekt operatora" : "Dodaj projekt operatora"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {globalError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
              <p className="text-sm text-red-700">{globalError}</p>
            </div>
          )}

          {isLoadingProject ? (
            <div className="text-center py-8" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">Ladowanie...</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nazwa projektu <span className="text-red-600">*</span>
                </label>
                <input
                  ref={firstInputRef}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.name ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="Przepis na rozwoj - Akademia HR"
                  maxLength={255}
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Konkurs <span className="text-red-600">*</span>
                  </label>
                  <CompetitionAutocomplete
                    competitions={(competitionsData?.competitions || []) as CompetitionOption[]}
                    value={formData.competitionId}
                    onChange={(competitionId) =>
                      setFormData({ ...formData, competitionId })
                    }
                    hasError={!!errors.competitionId}
                    disabled={isLoadingProject || isLoadingCompetitions}
                    isLoading={isLoadingCompetitions}
                  />
                  {errors.competitionId && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.competitionId}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Operator <span className="text-red-600">*</span>
                  </label>
                  <OperatorAutocomplete
                    operators={(operatorsData?.operators || []) as OperatorOption[]}
                    value={formData.operatorId}
                    onChange={(operatorId) =>
                      setFormData({ ...formData, operatorId })
                    }
                    hasError={!!errors.operatorId}
                    disabled={isLoadingProject || isLoadingOperators}
                    isLoading={isLoadingOperators}
                  />
                  {errors.operatorId && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.operatorId}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Regiony
                </label>
                <RegionsMultiSelect
                  options={REGION_OPTIONS}
                  value={formData.regions}
                  onChange={(regions) =>
                    setFormData({ ...formData, regions })
                  }
                />
              </div>

              {formData.regions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Powiaty (opcjonalnie)
                  </label>
                  <SubregionsMultiSelect
                    districts={districtsData?.districts || []}
                    value={formData.subregions}
                    onChange={(subregions) =>
                      setFormData({ ...formData, subregions })
                    }
                    isLoading={isLoadingDistricts}
                    isError={isDistrictsError}
                    onRetry={() => refetchDistricts()}
                  />
                  {errors.subregions ? (
                    <p className="mt-1 text-xs text-red-600">{errors.subregions}</p>
                  ) : (
                    <p className="mt-1 text-xs text-neutral-500">
                      Pozostaw puste dla całego regionu
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Strona projektu
                </label>
                <input
                  type="url"
                  value={formData.projectUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, projectUrl: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="https://example.com"
                  maxLength={500}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Data rozpoczecia
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Data zakonczenia
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {projectId && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="isActive" className="ml-2 text-sm text-neutral-700">
                    Projekt aktywny
                  </label>
                </div>
              )}
            </>
          )}
        </form>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isLoadingProject}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <div role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="sr-only">Zapisywanie...</span>
              </div>
            )}
            {isLoading
              ? "Zapisywanie..."
              : projectId
              ? "Zaktualizuj"
              : "Dodaj projekt"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProjectModal;
